import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useState } from "react";

import { ApiReportSectionsAiGeneratedStatusChoices } from "@api/graphql/types-and-hooks";

/**
 * LaunchDarkly number flag (minutes). How long a section may sit at
 * SECTION_PREDICTION_PENDING before the UI stops implying progress and offers
 * Regenerate instead. Tunable at runtime so support can widen or narrow the wait
 * without a deploy. (PROA-2110)
 *
 * The flag currently serves 0, which is rejected below in favour of the default —
 * set it to a positive number of minutes in LaunchDarkly to take effect.
 */
export const SECTION_STALL_THRESHOLD_FLAG = "report-section-regenerate-threshold";

export const DEFAULT_SECTION_STALL_THRESHOLD_MINUTES = 10;

/** Shape this module needs from a report section — keeps it usable with any generated type. */
export interface StallableSection {
  aiGeneratedStatus?: ApiReportSectionsAiGeneratedStatusChoices | null;
  lastUpdatedDate?: string | null;
}

// setTimeout fires immediately for delays above 2^31 - 1 ms (~24.8 days). A threshold that
// large is effectively "never stall", so skip the timer rather than re-render at once.
const MAX_TIMEOUT_MS = 2 ** 31 - 1;

/**
 * Epoch ms after which the section counts as stalled, or null when it can't stall (not
 * pending, or no usable `lastUpdatedDate`).
 *
 * `lastUpdatedDate` is `auto_now` on the model and the transition into
 * SECTION_PREDICTION_PENDING is itself a save, so for a section nobody has touched since
 * it started generating this is the generation start time.
 */
const getSectionStallDeadline = (section: StallableSection, thresholdMinutes: number): number | null => {
  if (section.aiGeneratedStatus !== ApiReportSectionsAiGeneratedStatusChoices.SectionPredictionPending) {
    return null;
  }
  if (!section.lastUpdatedDate) {
    return null;
  }
  const startedAt = new Date(section.lastUpdatedDate).getTime();
  if (Number.isNaN(startedAt)) {
    return null;
  }
  return startedAt + thresholdMinutes * 60_000;
};

/**
 * A section counts as stalled when it is still SECTION_PREDICTION_PENDING and has been
 * for longer than the threshold.
 *
 * The server deliberately leaves the status at SECTION_PREDICTION_PENDING rather than
 * flipping it to FAILURE: the report poll keys off that status, so flipping it would stop
 * polling and strand a late-but-successful generation in the UI.
 */
export const isSectionStalled = (section: StallableSection, thresholdMinutes: number): boolean => {
  const stallsAt = getSectionStallDeadline(section, thresholdMinutes);
  return stallsAt !== null && Date.now() > stallsAt;
};

/** Resolve the threshold from LaunchDarkly, ignoring malformed variations. */
export const useSectionStallThresholdMinutes = (): number => {
  const flags = useFlags();
  const raw = Number(flags[SECTION_STALL_THRESHOLD_FLAG]);
  // A zero/negative threshold would mark every section stalled the instant it starts.
  return Number.isFinite(raw) && raw > 0 ? raw : DEFAULT_SECTION_STALL_THRESHOLD_MINUTES;
};

/**
 * Whether this section should show the stalled treatment.
 *
 * Schedules a re-render for the moment the threshold passes. The report poll can't be
 * relied on for that: a wedged section returns identical data on every poll, TanStack
 * Query keeps the same data reference, and nothing re-renders unless some component
 * happens to read a field that changed (e.g. `isFetching`).
 */
export const useIsSectionStalled = (section: StallableSection): boolean => {
  const thresholdMinutes = useSectionStallThresholdMinutes();
  const stallsAt = getSectionStallDeadline(section, thresholdMinutes);
  // The deadline the timer below has seen pass. Keyed by value so a regeneration (which
  // moves the deadline) doesn't inherit the previous run's stalled state.
  const [passedDeadline, setPassedDeadline] = useState<number | null>(null);

  useEffect(() => {
    if (stallsAt === null) return;
    const delay = stallsAt - Date.now();
    if (delay < 0 || delay > MAX_TIMEOUT_MS) return;
    // +1 so the timer fires strictly past the deadline, matching isSectionStalled's `>`.
    const timeout = setTimeout(() => setPassedDeadline(stallsAt), delay + 1);
    return () => clearTimeout(timeout);
  }, [stallsAt]);

  return stallsAt !== null && (passedDeadline === stallsAt || isSectionStalled(section, thresholdMinutes));
};
